import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { generateTempId } from '../lib/offline/offlineDb';
import { useOfflineMutation } from './useOfflineMutation';
import type { Product } from '../types/product';

export interface Favorite {
  id: number | string;
  product_id: number;
  product: {
    id: number;
    name: string;
    article_number: string;
    category: { id: number; name: string } | null;
    partner: { id: number; name: string } | null;
    indications: { id: number; name: string }[];
    marketingClaims: { id: number; name: string }[];
    is_new: boolean;
    novel_food_status: string | null;
  };
  created_at: string;
}

export function useFavorites(enabled = true) {
  return useQuery<Favorite[]>({
    queryKey: ['favorites'],
    queryFn: () => api<Favorite[]>('/favorites'),
    enabled,
    // Only poll while online — see VIVATISPDB-305.
    refetchInterval: () => (typeof navigator !== 'undefined' && navigator.onLine ? 5 * 60 * 1000 : false),
  });
}

function productToFavoriteProduct(p: Product): Favorite['product'] {
  return {
    id: p.id,
    name: p.name,
    article_number: p.articleNumber,
    category: p.category,
    partner: p.partner,
    indications: p.indications,
    marketingClaims: p.marketingClaims,
    is_new: p.isNew,
    novel_food_status: p.novelFoodStatus,
  };
}

function findProductInCache(queryClient: ReturnType<typeof useQueryClient>, productId: number): Favorite['product'] | null {
  // Try product catalog cache (primary source in local-first architecture)
  const catalog = queryClient.getQueryData<Product[]>(['product-catalog']);
  if (catalog) {
    const found = catalog.find((p) => p.id === productId);
    if (found) return productToFavoriteProduct(found);
  }

  return null;
}

const emptyProduct: Favorite['product'] = {
  id: 0,
  name: '',
  article_number: '',
  category: null,
  partner: null,
  indications: [],
  marketingClaims: [],
  is_new: false,
  novel_food_status: null,
};

export function useToggleFavorite() {
  const queryClient = useQueryClient();

  return useOfflineMutation<number>({
    operationType: 'FAVORITE_TOGGLE',
    feature: 'favorites',
    mutationFn: (productId) =>
      api('/favorites/toggle', {
        method: 'POST',
        body: JSON.stringify({ product_id: productId }),
      }),
    toPayload: (productId) => ({ productId }),
    invalidateKeys: [['favorites'], ['favorite-groups']],
    onMutate: async (productId) => {
      await queryClient.cancelQueries({ queryKey: ['favorites'] });
      const previous = queryClient.getQueryData<Favorite[]>(['favorites']);
      const old = previous ?? [];

      const exists = old.some((f) => f.product_id === productId);
      const updated = exists
        ? old.filter((f) => f.product_id !== productId)
        : [
            ...old,
            {
              id: generateTempId(),
              product_id: productId,
              product: findProductInCache(queryClient, productId) ?? { ...emptyProduct, id: productId },
              created_at: new Date().toISOString(),
            },
          ];

      queryClient.setQueryData(['favorites'], updated);
      return { previous };
    },
    onError: (_err, _productId, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['favorites'], context.previous);
      }
    },
  });
}
